import { useRef, useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";

type UseEqualHeightOptions = {
  disableOnCondition?: boolean;
};

export const useEqualHeight = (
  options: UseEqualHeightOptions = {}
): {
  registerRef: (el: HTMLDivElement | null, groupKey: string) => void;
  height: number | "auto";
} => {
  const { disableOnCondition = false } = options;

  const groups = useRef<Map<string, Set<HTMLDivElement>>>(new Map());
  const observers = useRef<Map<HTMLDivElement, ResizeObserver>>(new Map());
  const rafId = useRef<number | null>(null);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);
  const [height, setHeight] = useState<number | "auto">(0);

  const resetChildHeights = useCallback((el: HTMLDivElement) => {
    Array.from(el.children).forEach((child) => {
      (child as HTMLElement).style.height = "auto";
    });
  }, []);

  const resetAllHeights = useCallback(() => {
    groups.current.forEach((elements) => {
      elements.forEach(resetChildHeights);
    });
  }, [resetChildHeights]);

  const applyHeightToChildren = useCallback((el: HTMLDivElement, h: number) => {
    Array.from(el.children).forEach((child) => {
      (child as HTMLElement).style.height = `${h}px`;
    });
  }, []);

  const measureHeights = useCallback(() => {
    if (disableOnCondition) return 0;

    if (rafId.current) cancelAnimationFrame(rafId.current);

    let globalMaxHeight = 0;

    groups.current.forEach((elements) => {
      let maxHeight = 0;

      elements.forEach((el) => {
        resetChildHeights(el);
        const childHeights = Array.from(el.children).map(
          (c) => (c as HTMLElement).scrollHeight
        );
        const elMax = childHeights.length ? Math.max(...childHeights) : 0;
        if (elMax > maxHeight) maxHeight = elMax;
      });

      elements.forEach((el) => applyHeightToChildren(el, maxHeight));

      if (maxHeight > globalMaxHeight) globalMaxHeight = maxHeight;
    });

    return globalMaxHeight;
  }, [disableOnCondition, resetChildHeights, applyHeightToChildren]);

  const scheduleMeasure = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);

    rafId.current = requestAnimationFrame(() => {
      const newMaxHeight = measureHeights();
      setHeight(newMaxHeight);
    });
  }, [measureHeights]);

  const registerRef = useCallback(
    (el: HTMLDivElement | null, groupKey: string) => {
      if (disableOnCondition) {
        resetAllHeights();
        setHeight("auto");
        return;
      }

      if (el) {
        if (!groups.current.has(groupKey)) {
          groups.current.set(groupKey, new Set());
        }
        groups.current.get(groupKey)!.add(el);

        if (!observers.current.has(el)) {
          const observer = new ResizeObserver(debounce(() => scheduleMeasure()));
          observer.observe(el);
          observers.current.set(el, observer);
        }
      } else {
        groups.current.forEach((elements, key) => {
          elements.forEach((e) => {
            if (!e.isConnected) {
              elements.delete(e);
              observers.current.get(e)?.disconnect();
              observers.current.delete(e);
            }
          });
          if (elements.size === 0) {
            groups.current.delete(key);
          }
        });
      }

      scheduleMeasure();
    },
    [disableOnCondition, resetAllHeights, scheduleMeasure]
  );

  useEffect(() => {
    if (disableOnCondition) {
      observers.current.forEach((obs) => obs.disconnect());
      observers.current.clear();
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      resetAllHeights();
      setHeight("auto");
      return;
    }

    const handleResize = () => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      resizeTimeout.current = setTimeout(scheduleMeasure, 50);
    };

    const currentObservers = observers.current;
    const currentRafId = rafId.current;

    window.addEventListener("resize", handleResize);
    scheduleMeasure();

    return () => {
      window.removeEventListener("resize", handleResize);
      currentObservers.forEach((obs) => obs.disconnect());
      currentObservers.clear();
      if (currentRafId !== null) cancelAnimationFrame(currentRafId);
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    };
  }, [disableOnCondition, resetAllHeights, scheduleMeasure]);

  return { registerRef, height };
};
